import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useSelector } from 'react-redux';
import { selectUserFacilities } from '../../redux/slices/facilitiesSlice';
import {
  getMasterWorkflowConfig,
  updateMasterWorkflowConfig,
  getFacilityWorkflowConfig,
  updateFacilityWorkflowConfig
} from '../../services/workflowConfigService';
import { getAllFacilities } from '../../services/labIntegrationService';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Select,
  MenuItem,
  TextField,
  InputLabel,
  FormControl,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Drawer,
  Divider,
  Snackbar,
  Tooltip,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { USER_ROLES, USER_ROLE_DISPLAY_NAMES, UserRole } from '../../types/enums';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewSidebarIcon from '@mui/icons-material/ViewSidebar';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import PersonIcon from '@mui/icons-material/Person';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import ScienceIcon from '@mui/icons-material/Science';
import MedicationIcon from '@mui/icons-material/Medication';
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import GroupIcon from '@mui/icons-material/Group';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import HelpIcon from '@mui/icons-material/Help';
import EditIcon from '@mui/icons-material/Edit';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import ClearIcon from '@mui/icons-material/Clear';
import { useUserData} from '../../hooks/useUserData';

// Styled Components - Modern Theme
const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
  color: 'white',
  marginBottom: theme.spacing(3),
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(25, 118, 210, 0.3)',
}));

const ModernCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  marginBottom: theme.spacing(3),
}));

const RESPONSIBLE_ROLES = USER_ROLES.filter(
  r => r !== UserRole.ADMIN && r !== UserRole.FACILITY_ADMIN
);

const REFERENCE_POINTS = [
  'After Admission',
  'After Discharge',
  'After Previous Step',
];

const TIMING_VALUE_OPTIONS = {
  minutes: [30, 60, 90, 120, 150, 180],
  hours: [4, 8, 12],
  days: Array.from({ length: 15 }, (_, i) => i + 1),
  weeks: [1, 2, 3],
  months: Array.from({ length: 12 }, (_, i) => i + 1),
};

const PHASE_OPTIONS = ['Admission', 'Ongoing', 'Discharge'];

const PHASE_COLORS = {
  Admission: '#1976d2',
  Ongoing: '#388e3c',
  Discharge: '#f57c00',
};

const ROLE_ICONS = {
  doctor: <LocalHospitalIcon color="primary" />,
  nurse: <MedicalServicesIcon color="success" />,
  pharmacist: <MedicationIcon color="secondary" />,
  social_worker: <GroupIcon color="info" />,
  case_manager: <AssignmentIndIcon color="info" />,
  physical_therapist: <AccessibilityNewIcon color="warning" />,
  occupational_therapist: <AccessibilityNewIcon color="warning" />,
  speech_language_pathologist: <AccessibilityNewIcon color="warning" />,
  dietitian: <ScienceIcon color="success" />,
  user: <PersonIcon color="action" />,
  attending_physician: <LocalHospitalIcon color="primary" />,
  unit_clerk: <PersonIcon color="action" />,
  bed_manager: <PersonIcon color="action" />,
  admission_clerk: <PersonIcon color="action" />,
  billing_specialist: <PersonIcon color="action" />,
};

const InpatientWorkflowConfig = () => {
  const { user} = useUserData();
  const { loading: authLoading } = useAuth();
  const userFacilities = useSelector(selectUserFacilities);
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [selectedFacility, setSelectedFacility] = useState('');
  const [facilities, setFacilities] = useState([
    { id: '', name: 'Master (Default)' }
  ]);
  const [viewMode, setViewMode] = useState('stepper'); // 'card' or 'stepper'
  const [selectedStepIdx, setSelectedStepIdx] = useState(null);
  
  // Multi-select state
  const [selectedSteps, setSelectedSteps] = useState(new Set());
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkEditField, setBulkEditField] = useState('');
  const [bulkEditValue, setBulkEditValue] = useState('');
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (!selectedFacility) {
        const data = await getMasterWorkflowConfig();
        setSteps(data.steps || []);
      } else {
        const data = await getFacilityWorkflowConfig(selectedFacility);
        setSteps(data.steps || []);
      }
    } catch (e) {
      setError('Failed to load workflow config');
      console.error(e);
    }
    setLoading(false);
  }, [selectedFacility]);

  useEffect(() => {
    async function fetchFacilitiesAndSetDefault() {
      if (!user) return;
      
      const userRoles = user.roles || [user.role].filter(Boolean);
      
      if (userRoles.includes('admin')) {
        // Admin: show all facilities, default to master
        const allFacilities = await getAllFacilities();
        setFacilities([{ id: '', name: 'Master (Default)' }, ...allFacilities]);
        setSelectedFacility('');
      } else if (userRoles.includes('facility_admin')) {
        // Facility admin: show only assigned facilities
        const facilityIds = userFacilities.map(uf => uf.facilityId).filter(Boolean);
        if (facilityIds.length === 0) {
          setFacilities([{ id: '', name: 'Master (Default)' }]);
          setSelectedFacility('');
          return;
        }
        const allFacilities = await getAllFacilities();
        const assignedFacilities = allFacilities.filter(f => facilityIds.includes(f.id));
        setFacilities([{ id: '', name: 'Master (Default)' }, ...assignedFacilities]);
        setSelectedFacility(assignedFacilities[0]?.id || '');
      } else {
        // Default: show all facilities, default to master
        const allFacilities = await getAllFacilities();
        setFacilities([{ id: '', name: 'Master (Default)' }, ...allFacilities]);
        setSelectedFacility('');
      }
    }
    fetchFacilitiesAndSetDefault();
     
  }, [user, userFacilities]);

  useEffect(() => {
    if (user) {
      fetchConfig();
    }
     
  }, [selectedFacility, user, fetchConfig]);

  // Clear selection when steps change
  useEffect(() => {
    setSelectedSteps(new Set());
    setBulkEditMode(false);
  }, [steps]);

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      const token = await user.getIdToken();
      if (!selectedFacility) {
        await updateMasterWorkflowConfig(steps, token);
      } else {
        await updateFacilityWorkflowConfig(selectedFacility, steps, token);
      }
      setSnackbarOpen(true);
    } catch (e) {
      setError('Failed to save workflow config');
    }
    setSaving(false);
  }

  function handleStepChange(idx, field, value) {
    setSteps(prev => prev.map((step, i) => i === idx ? { ...step, [field]: value } : step));
  }

  const handleAddStep = () => {
    setSteps(prev => ([
      ...prev,
      {
        title: '',
        description: '',
        mandatory: false,
        phase: '',
        responsibleRole: '',
        timingType: '',
        timingValue: '',
        referencePoint: 'After Admission',
      }
    ]));
  };

  const handleSeedSampleData = () => {
    const sampleSteps = [
      {
        title: "Patient Admission",
        description: "Complete initial patient assessment and documentation",
        mandatory: true,
        phase: "Admission",
        responsibleRole: "nurse",
        timingType: "hours",
        timingValue: 4,
        referencePoint: "After Admission"
      },
      {
        title: "Physician Assessment",
        description: "Complete medical evaluation and treatment plan",
        mandatory: true,
        phase: "Admission",
        responsibleRole: "doctor",
        timingType: "hours",
        timingValue: 8,
        referencePoint: "After Admission"
      },
      {
        title: "Medication Review",
        description: "Review and reconcile patient medications",
        mandatory: true,
        phase: "Admission",
        responsibleRole: "pharmacist",
        timingType: "hours",
        timingValue: 12,
        referencePoint: "After Admission"
      },
      {
        title: "Care Plan Development",
        description: "Develop comprehensive care plan with patient goals",
        mandatory: true,
        phase: "Ongoing",
        responsibleRole: "case_manager",
        timingType: "days",
        timingValue: 1,
        referencePoint: "After Admission"
      },
      {
        title: "Daily Assessment",
        description: "Complete daily patient assessment and vital signs",
        mandatory: true,
        phase: "Ongoing",
        responsibleRole: "nurse",
        timingType: "hours",
        timingValue: 8,
        referencePoint: "After Previous Step"
      }
    ];
    setSteps(sampleSteps);
  };

  function handleRemoveStep(idx) {
    setSteps(prev => prev.filter((_, i) => i !== idx));
  }

  function handleDragEnd(result) {
    if (!result.destination) return;
    const reordered = Array.from(steps);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    setSteps(reordered);
  }

  // Multi-select functions
  const handleStepSelection = (stepIndex) => {
    const newSelected = new Set(selectedSteps);
    if (newSelected.has(stepIndex)) {
      newSelected.delete(stepIndex);
    } else {
      newSelected.add(stepIndex);
    }
    setSelectedSteps(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedSteps.size === steps.length) {
      setSelectedSteps(new Set());
    } else {
      setSelectedSteps(new Set(steps.map((_, index) => index)));
    }
  };

  const toggleBulkEditMode = () => {
    setBulkEditMode(!bulkEditMode);
    if (bulkEditMode) {
      // Exiting bulk edit mode, clear selection
      setSelectedSteps(new Set());
    }
  };

  const handleBulkEdit = (field, value) => {
    const newSteps = [...steps];
    selectedSteps.forEach(stepIndex => {
      newSteps[stepIndex] = { ...newSteps[stepIndex], [field]: value };
    });
    setSteps(newSteps);
    setBulkEditOpen(false);
    setBulkEditField('');
    setBulkEditValue('');
  };

  const handleBulkEditOpen = (field) => {
    setBulkEditField(field);
    setBulkEditValue('');
    setBulkEditOpen(true);
  };

  const handleBulkEditClose = () => {
    setBulkEditOpen(false);
    setBulkEditField('');
    setBulkEditValue('');
  };

  const getBulkEditOptions = (field) => {
    switch (field) {
      case 'mandatory':
        return [
          { value: true, label: 'Yes' },
          { value: false, label: 'No' },
          { value: 'Conditional', label: 'Conditional' },
          { value: 'Recommended', label: 'Recommended' }
        ];
      case 'phase':
        return PHASE_OPTIONS.map(opt => ({ value: opt, label: opt }));
      case 'responsibleRole':
        return RESPONSIBLE_ROLES.map(role => ({ value: role, label: USER_ROLE_DISPLAY_NAMES[role] }));
      case 'timingType':
        return [
          { value: 'minutes', label: 'Minutes' },
          { value: 'hours', label: 'Hours' },
          { value: 'days', label: 'Days' },
          { value: 'weeks', label: 'Weeks' },
          { value: 'months', label: 'Months' }
        ];
      case 'timingValue':
        // Get timing type from the first selected step to determine available values
        const firstSelectedStep = steps[Array.from(selectedSteps)[0]];
        const timingType = firstSelectedStep?.timingType;
        return (TIMING_VALUE_OPTIONS[timingType] || []).map(val => ({ value: val, label: val }));
      case 'referencePoint':
        return REFERENCE_POINTS.map(opt => ({ value: opt, label: opt }));
      default:
        return [];
    }
  };

  const handleBulkRemove = () => {
    const newSteps = steps.filter((_, index) => !selectedSteps.has(index));
    setSteps(newSteps);
    setSelectedSteps(new Set());
  };

  if (authLoading) return <Box p={4}><CircularProgress /> Checking authentication...</Box>;
  if (!user) return <Box p={4}><Alert severity="warning">Please log in to view workflow configuration.</Alert></Box>;

  return (
    <Box sx={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh', p: 4, pb: 10 }}>
      <HeaderCard>
        <CardContent>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Inpatient Workflow Configuration
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel sx={{ color: 'white' }}>Facility</InputLabel>
              <Select
                value={selectedFacility}
                label="Facility"
                onChange={e => setSelectedFacility(e.target.value)}
                sx={{ color: 'white', '.MuiOutlinedInput-notchedOutline': { borderColor: 'white' }, '.MuiSvgIcon-root': { color: 'white' } }}
                inputProps={{ style: { color: 'white' } }}
                MenuProps={{ PaperProps: { sx: { backgroundColor: '#1976d2', color: 'white' } } }}
              >
                {facilities.map(f => <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>)}
              </Select>
            </FormControl>
            <IconButton onClick={() => {
              setViewMode(viewMode === 'card' ? 'stepper' : 'card');
              setSelectedSteps(new Set());
              setBulkEditMode(false);
            }} color="primary" sx={{ ml: 1, color: 'white' }}>
              {viewMode === 'card' ? <ViewSidebarIcon /> : <ViewModuleIcon />}
            </IconButton>
            <Typography variant="body2" sx={{ color: 'white' }}>
              {viewMode === 'card' ? 'Card View' : 'Stepper View'}
            </Typography>
            
            {/* Bulk Edit Button */}
            <Button
              size="small"
              variant="outlined"
              onClick={toggleBulkEditMode}
              sx={{ 
                color: 'white', 
                borderColor: 'white', 
                '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' },
                ml: 'auto'
              }}
              startIcon={bulkEditMode ? <ClearIcon /> : <EditIcon />}
            >
              {bulkEditMode ? 'Exit Bulk Edit' : 'Bulk Edit'}
            </Button>
            
            {/* Multi-select controls - only show when in bulk edit mode */}
            {bulkEditMode && selectedSteps.size > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip 
                  label={`${selectedSteps.size} selected`} 
                  color="secondary" 
                  sx={{ color: 'white', backgroundColor: 'rgba(255,255,255,0.2)' }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleBulkRemove}
                  sx={{ color: 'white', borderColor: 'white', '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' } }}
                >
                  Remove Selected
                </Button>
              </Box>
            )}
          </Box>
        </CardContent>
      </HeaderCard>
      
      {/* Bulk Edit Toolbar */}
      {bulkEditMode && (
        <Card sx={{ mb: 3, backgroundColor: '#f3f4f6', border: '1px solid #e5e7eb' }}>
          <CardContent sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151' }}>
                Bulk Edit Mode
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={handleSelectAll}
                startIcon={<SelectAllIcon />}
              >
                {selectedSteps.size === steps.length ? 'Deselect All' : 'Select All'} ({steps.length} steps)
              </Button>
              {selectedSteps.size > 0 && (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleBulkEditOpen('mandatory')}
                    startIcon={<EditIcon />}
                  >
                    Mandatory
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleBulkEditOpen('phase')}
                    startIcon={<EditIcon />}
                  >
                    Phase
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleBulkEditOpen('responsibleRole')}
                    startIcon={<EditIcon />}
                  >
                    Responsible Role
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleBulkEditOpen('timingType')}
                    startIcon={<EditIcon />}
                  >
                    Timing Type
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleBulkEditOpen('timingValue')}
                    startIcon={<EditIcon />}
                    disabled={(() => {
                      if (selectedSteps.size === 0) return true;
                      const firstSelectedStep = steps[Array.from(selectedSteps)[0]];
                      return !firstSelectedStep?.timingType;
                    })()}
                  >
                    Timing Value
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleBulkEditOpen('referencePoint')}
                    startIcon={<EditIcon />}
                  >
                    Reference Point
                  </Button>
                </>
              )}
            </Box>
          </CardContent>
        </Card>
      )}
      <Box>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading workflow configuration...</Typography>
          </Box>
        ) : error ? (
          <Alert severity="error">
            <Typography variant="h6">Error Loading Workflow Configuration</Typography>
            <Typography>{error}</Typography>
            <Button 
              onClick={fetchConfig} 
              variant="outlined" 
              sx={{ mt: 2 }}
              startIcon={<AddIcon />}
            >
              Retry
            </Button>
          </Alert>
        ) : (
          viewMode === 'card' ? (
            <Box>
              {steps.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                    No workflow steps configured
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {selectedFacility ? 
                      'This facility has no custom workflow configuration. Using master configuration.' : 
                      'No master workflow configuration found. Please add some steps to get started.'
                    }
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAddStep}
                    startIcon={<AddIcon />}
                    sx={{ mr: 2 }}
                  >
                    Add First Step
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleSeedSampleData}
                    startIcon={<AddIcon />}
                  >
                    Load Sample Data
                  </Button>
                </Box>
              ) : (
                steps.map((step, idx) => (
                <ModernCard key={idx} sx={{ position: 'relative', mb: 3 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {bulkEditMode && (
                          <Checkbox
                            checked={selectedSteps.has(idx)}
                            onChange={() => handleStepSelection(idx)}
                            sx={{ ml: -1 }}
                          />
                        )}
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Step {idx + 1}
                        </Typography>
                      </Box>
                      <Button onClick={() => handleRemoveStep(idx)} color="error" variant="outlined" sx={{ borderRadius: '8px', fontWeight: 600 }}>Remove</Button>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      <TextField
                        label="Title"
                        value={step.title}
                        onChange={e => handleStepChange(idx, 'title', e.target.value)}
                        size="small"
                        variant="outlined"
                        sx={{ borderRadius: '8px', flex: 1, minWidth: 220 }}
                        required
                        helperText={!step.title ? 'Title is required' : ''}
                        error={!step.title}
                      />
                      <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Mandatory</InputLabel>
                        <Select
                          value={step.mandatory}
                          label="Mandatory"
                          onChange={e => handleStepChange(idx, 'mandatory', e.target.value)}
                        >
                          <MenuItem value={true}>Yes</MenuItem>
                          <MenuItem value={false}>No</MenuItem>
                          <MenuItem value="Conditional">Conditional</MenuItem>
                          <MenuItem value="Recommended">Recommended</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Phase</InputLabel>
                        <Select
                          value={step.phase || ''}
                          label="Phase"
                          onChange={e => handleStepChange(idx, 'phase', e.target.value)}
                        >
                          <MenuItem value=""><em>Select phase</em></MenuItem>
                          {PHASE_OPTIONS.map(opt => (
                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel>Responsible Role</InputLabel>
                        <Select
                          value={RESPONSIBLE_ROLES.includes(step.responsibleRole) ? step.responsibleRole : ''}
                          label="Responsible Role"
                          onChange={e => handleStepChange(idx, 'responsibleRole', e.target.value)}
                        >
                          <MenuItem value=""><em>Select role</em></MenuItem>
                          {RESPONSIBLE_ROLES.map(role => (
                            <MenuItem key={role} value={role}>{USER_ROLE_DISPLAY_NAMES[role]}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                      <TextField
                        label="Description"
                        value={step.description}
                        onChange={e => handleStepChange(idx, 'description', e.target.value)}
                        size="small"
                        variant="outlined"
                        sx={{ borderRadius: '8px', flex: 2, minWidth: 320 }}
                        multiline
                        rows={2}
                      />
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <FormControl size="small" sx={{ minWidth: 120 }}>
                            <InputLabel>Timing Type</InputLabel>
                            <Select
                              value={step.timingType || ''}
                              label="Timing Type"
                              onChange={e => handleStepChange(idx, 'timingType', e.target.value)}
                            >
                              <MenuItem value="minutes">Minutes</MenuItem>
                              <MenuItem value="hours">Hours</MenuItem>
                              <MenuItem value="days">Days</MenuItem>
                              <MenuItem value="weeks">Weeks</MenuItem>
                              <MenuItem value="months">Months</MenuItem>
                            </Select>
                          </FormControl>
                          <FormControl size="small" sx={{ minWidth: 100 }}>
                            <InputLabel>Timing Value</InputLabel>
                            <Select
                              value={step.timingValue || ''}
                              label="Timing Value"
                              onChange={e => handleStepChange(idx, 'timingValue', e.target.value)}
                              disabled={!step.timingType}
                            >
                              {(TIMING_VALUE_OPTIONS[step.timingType] || []).map(val => (
                                <MenuItem key={val} value={val}>{val}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel>Reference Point</InputLabel>
                            <Select
                              value={step.referencePoint || 'After Admission'}
                              label="Reference Point"
                              onChange={e => handleStepChange(idx, 'referencePoint', e.target.value)}
                            >
                              {REFERENCE_POINTS.map(opt => (
                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </ModernCard>
              )))}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
              <Box sx={{ minWidth: 320, maxWidth: 800, width: '100%' }}>
                {steps.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                      No workflow steps configured
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      {selectedFacility ? 
                        'This facility has no custom workflow configuration. Using master configuration.' : 
                        'No master workflow configuration found. Please add some steps to get started.'
                      }
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleAddStep}
                      startIcon={<AddIcon />}
                      sx={{ mr: 2 }}
                    >
                      Add First Step
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={handleSeedSampleData}
                      startIcon={<AddIcon />}
                    >
                      Load Sample Data
                    </Button>
                  </Box>
                ) : (
                  <>
                    {viewMode === 'stepper' && (
                      <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', mb: 2, ml: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 16, height: 16, bgcolor: PHASE_COLORS['Admission'], borderRadius: '4px' }} />
                          <Typography variant="caption">Admission</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 16, height: 16, bgcolor: PHASE_COLORS['Ongoing'], borderRadius: '4px' }} />
                          <Typography variant="caption">Ongoing</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 16, height: 16, bgcolor: PHASE_COLORS['Discharge'], borderRadius: '4px' }} />
                          <Typography variant="caption">Discharge</Typography>
                        </Box>
                      </Box>
                    )}
                    <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="stepper-droppable">
                      {(provided) => (
                        <Stepper
                          orientation="vertical"
                          activeStep={selectedStepIdx ?? -1}
                          sx={{ background: 'transparent', p: 2 }}
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                        >
                          {steps.map((step, idx) => (
                            <Draggable key={idx} draggableId={`step-${idx}`} index={idx}>
                              {(dragProvided, dragSnapshot) => (
                                <Step
                                  completed={false}
                                  sx={{
                                    background: selectedSteps.has(idx) ? '#e3f2fd' : (dragSnapshot.isDragging ? '#e3f2fd' : 'white'),
                                    borderRadius: 2,
                                    mb: 2,
                                    boxShadow: dragSnapshot.isDragging ? 4 : (selectedSteps.has(idx) ? 2 : 1),
                                    cursor: bulkEditMode ? 'default' : 'pointer',
                                    transition: 'box-shadow 0.2s',
                                    position: 'relative',
                                    borderLeft: step.phase && PHASE_COLORS[step.phase] ? `8px solid ${PHASE_COLORS[step.phase]}` : undefined,
                                    border: selectedSteps.has(idx) ? '2px solid #1976d2' : undefined,
                                  }}
                                  onClick={() => {
                                    if (!bulkEditMode) {
                                      setSelectedStepIdx(idx);
                                    }
                                  }}
                                  ref={dragProvided.innerRef}
                                  {...dragProvided.draggableProps}
                                >
                                  <StepLabel
                                    icon={
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {bulkEditMode && (
                                          <Checkbox
                                            checked={selectedSteps.has(idx)}
                                            onChange={(e) => {
                                              e.stopPropagation();
                                              handleStepSelection(idx);
                                            }}
                                            size="small"
                                            sx={{ ml: -1 }}
                                          />
                                        )}
                                        <span {...dragProvided.dragHandleProps} style={{ display: 'inline-flex', cursor: 'grab' }}>
                                          <DragIndicatorIcon />
                                        </span>
                                      </Box>
                                    }
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                      <Typography fontWeight={selectedStepIdx === idx ? 700 : 500}>
                                        {`${idx + 1}. ${step.title || `Step ${idx + 1}`}`}
                                      </Typography>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 32, justifyContent: 'flex-end' }}>
                                        {step.responsibleRole && (
                                          ROLE_ICONS[step.responsibleRole] || <PersonIcon color="action" />
                                        )}
                                        {step.mandatory === true && (
                                          <Tooltip title="Mandatory step">
                                            <CheckCircleIcon color="success" fontSize="small" sx={{ ml: 0.5 }} />
                                          </Tooltip>
                                        )}
                                        {step.mandatory === 'Recommended' && (
                                          <Tooltip title="Recommended step">
                                            <StarIcon color="warning" fontSize="small" sx={{ ml: 0.5 }} />
                                          </Tooltip>
                                        )}
                                        {step.mandatory === 'Conditional' && (
                                          <Tooltip title="Conditional step">
                                            <HelpIcon color="info" fontSize="small" sx={{ ml: 0.5 }} />
                                          </Tooltip>
                                        )}
                                      </Box>
                                    </Box>
                                    {step.description && (
                                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, mb: 0 }}>
                                        {step.description?.slice(0, 60)}
                                      </Typography>
                                    )}
                                    {step.timingValue && step.timingType && step.referencePoint && (
                                      <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.5, mb: 0 }}>
                                        {`Task due within ${step.timingValue} ${step.timingType} ${step.referencePoint}`}
                                      </Typography>
                                    )}
                                  </StepLabel>
                                </Step>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </Stepper>
                      )}
                    </Droppable>
                    </DragDropContext>
                  </>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ mt: 2, borderRadius: '50%', minWidth: 0, width: 56, height: 56, boxShadow: 6 }}
                  onClick={handleAddStep}
                  aria-label="Add Step"
                >
                  <AddIcon fontSize="large" />
                </Button>
              </Box>
              <Drawer
                anchor="right"
                open={selectedStepIdx !== null}
                onClose={() => setSelectedStepIdx(null)}
                PaperProps={{ sx: { width: 480, p: 3 } }}
              >
                {selectedStepIdx !== null && steps[selectedStepIdx] && (
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      Edit Step {selectedStepIdx + 1}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <TextField
                      label="Title"
                      value={steps[selectedStepIdx].title}
                      onChange={e => handleStepChange(selectedStepIdx, 'title', e.target.value)}
                      size="small"
                      variant="outlined"
                      sx={{ borderRadius: '8px', mb: 2, width: '100%' }}
                      required
                      helperText={!steps[selectedStepIdx].title ? 'Title is required' : ''}
                      error={!steps[selectedStepIdx].title}
                    />
                    <FormControl size="small" sx={{ minWidth: 160, mb: 2 }}>
                      <InputLabel>Mandatory</InputLabel>
                      <Select
                        value={steps[selectedStepIdx].mandatory}
                        label="Mandatory"
                        onChange={e => handleStepChange(selectedStepIdx, 'mandatory', e.target.value)}
                      >
                        <MenuItem value={true}>Yes</MenuItem>
                        <MenuItem value={false}>No</MenuItem>
                        <MenuItem value="Conditional">Conditional</MenuItem>
                        <MenuItem value="Recommended">Recommended</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 160, mb: 2 }}>
                      <InputLabel>Phase</InputLabel>
                      <Select
                        value={steps[selectedStepIdx].phase || ''}
                        label="Phase"
                        onChange={e => handleStepChange(selectedStepIdx, 'phase', e.target.value)}
                      >
                        <MenuItem value=""><em>Select phase</em></MenuItem>
                        {PHASE_OPTIONS.map(opt => (
                          <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 220, mb: 2 }}>
                      <InputLabel>Responsible Role</InputLabel>
                      <Select
                        value={RESPONSIBLE_ROLES.includes(steps[selectedStepIdx].responsibleRole) ? steps[selectedStepIdx].responsibleRole : ''}
                        label="Responsible Role"
                        onChange={e => handleStepChange(selectedStepIdx, 'responsibleRole', e.target.value)}
                      >
                        <MenuItem value=""><em>Select role</em></MenuItem>
                        {RESPONSIBLE_ROLES.map(role => (
                          <MenuItem key={role} value={role}>{USER_ROLE_DISPLAY_NAMES[role]}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      label="Description"
                      value={steps[selectedStepIdx].description}
                      onChange={e => handleStepChange(selectedStepIdx, 'description', e.target.value)}
                      size="small"
                      variant="outlined"
                      sx={{ borderRadius: '8px', mb: 2, width: '100%' }}
                      multiline
                      rows={2}
                    />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FormControl size="small" sx={{ minWidth: 120, mb: 2 }}>
                          <InputLabel>Timing Type</InputLabel>
                          <Select
                            value={steps[selectedStepIdx].timingType || ''}
                            label="Timing Type"
                            onChange={e => handleStepChange(selectedStepIdx, 'timingType', e.target.value)}
                          >
                            <MenuItem value="minutes">Minutes</MenuItem>
                            <MenuItem value="hours">Hours</MenuItem>
                            <MenuItem value="days">Days</MenuItem>
                            <MenuItem value="weeks">Weeks</MenuItem>
                            <MenuItem value="months">Months</MenuItem>
                          </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 100, mb: 2 }}>
                          <InputLabel>Timing Value</InputLabel>
                          <Select
                            value={steps[selectedStepIdx].timingValue || ''}
                            label="Timing Value"
                            onChange={e => handleStepChange(selectedStepIdx, 'timingValue', e.target.value)}
                            disabled={!steps[selectedStepIdx].timingType}
                          >
                            {(TIMING_VALUE_OPTIONS[steps[selectedStepIdx].timingType] || []).map(val => (
                              <MenuItem key={val} value={val}>{val}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                          <InputLabel>Reference Point</InputLabel>
                          <Select
                            value={steps[selectedStepIdx].referencePoint || 'After Admission'}
                            label="Reference Point"
                            onChange={e => handleStepChange(selectedStepIdx, 'referencePoint', e.target.value)}
                          >
                            {REFERENCE_POINTS.map(opt => (
                              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    </Box>
                    <Button
                      onClick={() => handleRemoveStep(selectedStepIdx)}
                      color="error"
                      variant="outlined"
                      sx={{ borderRadius: '8px', fontWeight: 600, mt: 2 }}
                      fullWidth
                    >
                      Remove Step
                    </Button>
                  </Box>
                )}
              </Drawer>
            </Box>
          )
        )}
      </Box>
      
      {/* Bulk Edit Dialog */}
      <Dialog open={bulkEditOpen} onClose={handleBulkEditClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Bulk Edit {bulkEditField && getBulkEditOptions(bulkEditField).length > 0 && (
            <Typography variant="body2" color="text.secondary">
              {selectedSteps.size} step{selectedSteps.size !== 1 ? 's' : ''} selected
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Set {bulkEditField === 'mandatory' ? 'Mandatory Status' : 
                    bulkEditField === 'phase' ? 'Phase' :
                    bulkEditField === 'responsibleRole' ? 'Responsible Role' :
                    bulkEditField === 'timingType' ? 'Timing Type' :
                    bulkEditField === 'timingValue' ? 'Timing Value' :
                    bulkEditField === 'referencePoint' ? 'Reference Point' : 'Field'} for all selected steps
            </Typography>
            
            {bulkEditField === 'timingType' && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Timing Type</InputLabel>
                <Select
                  value={bulkEditValue}
                  label="Timing Type"
                  onChange={(e) => setBulkEditValue(e.target.value)}
                >
                  {getBulkEditOptions('timingType').map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            
            {bulkEditField === 'timingValue' && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Timing Value</InputLabel>
                <Select
                  value={bulkEditValue}
                  label="Timing Value"
                  onChange={(e) => setBulkEditValue(e.target.value)}
                  disabled={getBulkEditOptions('timingValue').length === 0}
                >
                  {getBulkEditOptions('timingValue').map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {getBulkEditOptions('timingValue').length === 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    Please set Timing Type first for the selected steps
                  </Typography>
                )}
              </FormControl>
            )}
            
            {bulkEditField !== 'timingType' && bulkEditField !== 'timingValue' && (
              <FormControl fullWidth>
                <InputLabel>
                  {bulkEditField === 'mandatory' ? 'Mandatory Status' : 
                   bulkEditField === 'phase' ? 'Phase' :
                   bulkEditField === 'responsibleRole' ? 'Responsible Role' :
                   bulkEditField === 'referencePoint' ? 'Reference Point' : 'Value'}
                </InputLabel>
                <Select
                  value={bulkEditValue}
                  label={bulkEditField === 'mandatory' ? 'Mandatory Status' : 
                         bulkEditField === 'phase' ? 'Phase' :
                         bulkEditField === 'responsibleRole' ? 'Responsible Role' :
                         bulkEditField === 'referencePoint' ? 'Reference Point' : 'Value'}
                  onChange={(e) => setBulkEditValue(e.target.value)}
                >
                  {getBulkEditOptions(bulkEditField).map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBulkEditClose}>Cancel</Button>
          <Button 
            onClick={() => handleBulkEdit(bulkEditField, bulkEditValue)} 
            variant="contained"
            disabled={!bulkEditValue}
          >
            Apply to {selectedSteps.size} step{selectedSteps.size !== 1 ? 's' : ''}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for save confirmation */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message="Workflow config saved!"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
      
      <Button
        onClick={handleSave}
        disabled={saving}
        variant="contained"
        color="secondary"
        startIcon={<SaveIcon />}
        sx={{
          position: 'fixed',
          bottom: 32,
          right: 104,
          borderRadius: '28px',
          fontWeight: 600,
          px: 4,
          py: 1.5,
          boxShadow: 6,
          zIndex: 1201
        }}
      >
        {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
      </Button>
    </Box>
  );
};

export default InpatientWorkflowConfig; 